import {faHouse, faUserGroup} from '@fortawesome/pro-duotone-svg-icons';
import {faXmark} from '@fortawesome/pro-regular-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import type {IconButtonProps} from '@mui/material/IconButton';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import Typography from '@mui/material/Typography';
import {CanAccess, useGetIdentity} from '@refinedev/core';
import {useBoolean} from 'minimal-shared/hooks';
import {ReactNode} from 'react';
import {useTranslation} from 'react-i18next';

import {User} from '@app/types';
import {AnimateBorder} from '@minimal/components/animate';
import {Scrollbar} from '@minimal/components/scrollbar';
import {RouterLink} from '@minimal/routes/components';

import {AccountButton} from './account-button';
import {SignOutButton} from './sign-out-button';

// ----------------------------------------------------------------------

type LinkType = {
  label: string;
  href: string;
  resource: string;
  action: string;
  icon?: ReactNode;
};

export type AccountDrawerProps = IconButtonProps;

export function AccountDrawer({sx, ...other}: AccountDrawerProps) {
  const {data: user} = useGetIdentity<User>();
  const {t} = useTranslation();

  const {value: open, onFalse: onClose, onTrue: onOpen} = useBoolean();

  const data: LinkType[] = [
    {
      label: 'pages.dashboard.drawer.dashboard',
      href: '/',
      resource: '',
      action: '',
      icon: <FontAwesomeIcon icon={faHouse} />,
    },
    {
      label: 'pages.dashboard.drawer.manage_users',
      href: '/users',
      resource: 'global/records/user',
      action: 'list',
      icon: <FontAwesomeIcon icon={faUserGroup} />,
    },
  ];

  const renderAvatar = () => (
    <AnimateBorder
      sx={{mb: 2, p: '6px', width: 96, height: 96, borderRadius: '50%'}}
      slotProps={{
        primaryBorder: {size: 120, sx: {color: 'primary.main'}},
        secondaryBorder: {sx: {color: 'warning.main'}},
      }}
    >
      <Avatar src={user?.avatar} alt={user?.name} sx={{width: 1, height: 1}}>
        {user?.name?.charAt(0).toUpperCase()}
      </Avatar>
    </AnimateBorder>
  );

  const renderList = () => (
    <MenuList
      disablePadding
      sx={[
        (theme) => ({
          py: 3,
          px: 2.5,
          borderTop: `dashed 1px ${theme.vars.palette.divider}`,
          borderBottom: `dashed 1px ${theme.vars.palette.divider}`,
          '& li': {p: 0},
        }),
      ]}
    >
      {data.map((option) => {
        return (
          <CanAccess key={option.label} resource={option.resource} action={option.action}>
            <MenuItem>
              <Link
                component={RouterLink}
                href={option.href}
                color="inherit"
                underline="none"
                onClick={onClose}
                sx={{
                  p: 1,
                  width: 1,
                  display: 'flex',
                  typography: 'body2',
                  alignItems: 'center',
                  color: 'text.secondary',
                  '& svg': {width: 24, height: 24},
                  '&:hover': {color: 'text.primary'},
                }}
              >
                {option.icon}

                <Box component="span" sx={{ml: 2}}>
                  {t(option.label)}
                </Box>
              </Link>
            </MenuItem>
          </CanAccess>
        );
      })}
    </MenuList>
  );

  return (
    <>
      <AccountButton onClick={onOpen} photoURL={user?.avatar} displayName={user?.name} sx={sx} {...other} />

      <Drawer
        open={open}
        onClose={onClose}
        anchor="right"
        slotProps={{backdrop: {invisible: true}}}
        PaperProps={{sx: {width: 320}}}
      >
        <IconButton
          onClick={onClose}
          sx={{
            top: 12,
            left: 12,
            zIndex: 9,
            position: 'absolute',
          }}
        >
          <FontAwesomeIcon icon={faXmark} />
        </IconButton>

        <Scrollbar>
          <Box
            sx={{
              pt: 8,
              display: 'flex',
              alignItems: 'center',
              flexDirection: 'column',
            }}
          >
            {renderAvatar()}

            <Typography variant="subtitle1" noWrap sx={{mt: 2}}>
              {user?.name}
            </Typography>

            <Typography variant="body2" sx={{color: 'text.secondary', mt: 0.5}} noWrap>
              {user?.email}
            </Typography>
          </Box>

          {renderList()}
        </Scrollbar>

        <Box sx={{p: 2.5}}>
          <SignOutButton />
        </Box>
      </Drawer>
    </>
  );
}
